<template>
  <v-card class="pa-2">
    <v-row dense class="ma-0 d-flex justify-end">
      <v-btn
        depressed
        color="#0f3565"
        dark
        class="mr-0 hidden-sm-and-down primary-button"
        @click="filtersVisible = !filtersVisible"
      >
        Filtros
      </v-btn>
    </v-row>

    <v-expand-transition>
      <div v-if="filtersVisible">
        <v-row dense class="ma-0" align="center" justify="end">
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Unidade') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.unidadeId"
              :items="unidades"
              item-value="id"
              item-text="name"
              :loading="unidadeLoading"
              outlined
              class="primary-input"
              placeholder="Todas"
              @keyup.enter="filterTable"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Setor') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.departmentId"
              :items="departments"
              item-value="id"
              item-text="name"
              :loading="departmentLoading"
              outlined
              class="primary-input"
              placeholder="Todos"
              @keyup.enter="filterTable"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Centro de custo') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.costCenterId"
              :items="costCenters"
              item-value="id"
              item-text="name"
              outlined
              class="primary-input"
              placeholder="Todos"
              @keyup.enter="filterTable"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Tipo') }}</label>
            <v-autocomplete
              hide-details
              clearable
              v-model="filter.type"
              :items="typeOptions"
              placeholder="Tipo"
              item-text="text"
              item-value="value"
              outlined
              class="primary-input"
              append-icon="mdi-chevron-down"
              @keyup.enter="filterTable"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Natureza') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.natureId"
              :items="natures"
              item-text="nature"
              item-value="id"
              class="primary-input"
              placeholder="Filtrar por natureza"
              outlined
              @keyup.enter="filterTable"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-row no-gutters class="ma-0">
              <v-col cols="12" class="text-center">
                <label class="label">{{
                  $t('Período do custo evitado')
                }}</label>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  ref="menuStartMonth"
                  v-model="startMonthMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      hide-details="auto"
                      v-model="filter.startMonth"
                      placeholder="Mês Inicial"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      autocomplete="off"
                      outlined
                      class="primary-input mr-1"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startMonthISO"
                    type="month"
                    min="1900-01"
                    @change="saveStartMonth"
                    no-title
                    locale="pt"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <v-menu
                  ref="menuEndMonth"
                  v-model="endMonthMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      hide-details="auto"
                      v-model="filter.endMonth"
                      placeholder="Mês Final"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      autocomplete="off"
                      outlined
                      class="primary-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="endMonthISO"
                    type="month"
                    @change="saveEndMonth"
                    locale="pt"
                    no-title
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-col>

          <v-col>
            <v-btn class="primary-button ma-1 mb-4" depressed @click="list">
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div class="d-flex justify-end" v-if="downloadList.length > 0">
        <v-btn depressed class="secondary-button" @click="getZip">
          Baixar arquivos ({{ downloadList.length }} selecionados)
        </v-btn>
      </div>
    </v-expand-transition>

    <v-data-table
      :loading="loading"
      :loading-text="$t('Carregando...')"
      :no-data-text="$t('Nenhum arquivo encontrado')"
      :headers="headers"
      :items="avoidedCosts"
      class="main-table"
      hide-default-footer
      :items-per-page="filter.pageSize"
      :header-props="headerProps"
      :single-expand="singleExpand"
      :expanded.sync="expanded"
      show-expand
      hide-default-header
    >
      <template #header="{ props, on }">
        <thead>
          <tr>
            <th
              class="text-center"
              v-for="header in props.headers"
              :key="header.value"
            >
              <v-simple-checkbox
                v-if="header.value === 'checkbox'"
                v-model="props.everyItem"
                :indeterminate="props.someItems && !props.everyItem"
                color="white"
                dark
                @input="on['toggle-select-all']"
              ></v-simple-checkbox>

              <div v-else>
                <v-hover v-slot="{ hover }">
                  <div>
                    <span>{{ $t(header.text) }}</span>
                    <v-icon
                      v-if="
                        hover &&
                        header.value != 'actions' &&
                        header.value != 'checkbox'
                      "
                      color="white"
                      class="mx-2"
                      @click="on.sort(header.value)"
                    >
                      {{
                        props.options.sortBy.includes(header.value)
                          ? `mdi-chevron-down`
                          : `mdi-chevron-up`
                      }}
                    </v-icon>
                  </div>
                </v-hover>
              </div>
            </th>
          </tr>
        </thead>
      </template>
      <template #[`header.checkbox`]>
        <div class="d-flex justify-center align-center">
          <v-checkbox
            dense
            dark
            hide-details
            :value="downloadList.length == filesNumber"
            @click="selectAll"
          ></v-checkbox>
        </div>
      </template>

      <template #[`item.type`]="{ item }">
        {{
          item.type !== null && item.type !== undefined
            ? $t(
                typeOptions.find((t) => t.value === item.type)?.text ||
                  'Sem registro'
              )
            : $t('Sem registro')
        }}
      </template>

      <template #[`item.cost`]="{ item }">
        {{ formatCurrency(item.cost) }}
      </template>

      <template #[`item.uploadDate`]="{ item }">
        {{
          item.uploadDocuments && item.uploadDocuments.length > 0
            ? formatDate(getMostRecentUploadDate(item.uploadDocuments))
            : $t('Sem registro')
        }}
      </template>

      <template #[`item.checkbox`]="{ item }">
        <div class="d-flex justify-center">
          <v-checkbox
            dense
            v-model="item.selected"
            @click="addAllToDownloadList(item)"
          ></v-checkbox>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <table class="main-table" style="width: 100%">
            <thead>
              <tr>
                <th>
                  <v-icon color="white">mdi-download</v-icon>
                </th>
                <th
                  style="
                    min-width: unset !important;
                    width: unset !important;
                    max-width: unset !important;
                  "
                  class="pa-2"
                >
                  Nome do arquivo
                </th>
                <th class="pa-2">Tipo de arquivo</th>
                <th class="pa-2">Data do Upload</th>
                <th class="pa-2">Ações</th>
              </tr>
            </thead>
            <tr v-for="file in item.uploadDocuments" :key="file.id">
              <td>
                <v-checkbox
                  v-model="file.selected"
                  @click="addToDownloadList(file)"
                ></v-checkbox>
              </td>
              <td
                style="
                  min-width: unset !important;
                  width: unset !important;
                  max-width: unset !important;
                "
                class="text-center"
              >
                {{ file.name }}
              </td>
              <td class="text-center">.{{ file.fileType }}</td>
              <td class="text-center">{{ formatDate(file.uploadDate) }}</td>
              <td class="text-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      class="primary-button"
                      tile
                      small
                      depressed
                      @click="getFile(file)"
                    >
                      <v-icon>mdi-open-in-new</v-icon>
                    </v-btn>
                  </template>
                  <span>Abrir documento</span>
                </v-tooltip>
              </td>
            </tr>
          </table>
        </td>
      </template>
    </v-data-table>

    <div class="pagination">
      <div class="item-pagination block-one">
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="btn"
              depressed
              color="#C0C0C0"
              small
            >
              qtd: {{ pageSize }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(size, i) in sizes"
              :key="i"
              @click="pageSize = size"
            >
              <v-list-item-title>{{ size }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <div class="registros" v-if="totalItensOnPage">
          <span> 1 - {{ totalItensOnPage }} de {{ totalItems }} </span> <br />
        </div>
      </div>

      <div class="item-pagination block-two">
        <v-pagination
          class="pagination-item"
          v-model="page"
          :length="totalPages"
          :total-visible="3"
        ></v-pagination>
      </div>
    </div>
  </v-card>
</template>

<script>
import ApiService from '../../../../services/ApiService'
import moment from 'moment'

export default {
  name: 'avoidedCostRepositoryDocuments',

  data() {
    return {
      filtersVisible: false,
      unidadeLoading: false,
      departmentLoading: false,
      unidades: [],
      departments: [],
      allDepartments: [],
      costCenters: [],
      natures: [],
      page: 1,
      totalPages: null,
      totalItensOnPage: 0,
      totalItems: 0,
      pageSize: 10,
      sizes: [10, 30, 90, 180],
      downloadList: [],
      expanded: [],
      startMonthMenu: false,
      endMonthMenu: false,
      startMonthISO: null,
      endMonthISO: null,
      singleExpand: true,
      typeOptions: [
        { text: 'Unitário', value: 0 },
        { text: 'Recorrente', value: 1 },
      ],
      headers: [
        {
          text: 'Seleção',
          align: 'center',
          value: 'checkbox',
          sortable: false,
          width: 40,
        },
        {
          text: 'Tipo',
          align: 'center',
          value: 'type',
        },
        {
          text: 'Custo',
          align: 'center',
          value: 'cost',
        },
        {
          text: 'Descrição',
          align: 'center',
          value: 'description',
        },
        {
          text: 'Natureza',
          align: 'center',
          value: 'avoidedCostNature.nature',
        },
        {
          text: 'Data',
          align: 'center',
          value: 'uploadDate',
        },
      ],
      apiService: new ApiService(),
      loading: false,
      headerProps: {
        sortByText: 'Ordenar por',
      },
      filter: {
        page: 1,
        pageSize: 10,
        unidadeId: null,
        departmentId: null,
        costCenterId: null,
        type: null,
        natureId: null,
        startMonth: '',
        endMonth: '',
        startDate: null,
        endDate: null,
      },
      avoidedCosts: [],
      institutionId: null,
    }
  },

  computed: {
    filesNumber() {
      let filesCount = 0
      this.avoidedCosts.forEach((item) => {
        if (item.uploadDocuments) {
          filesCount += item.uploadDocuments.length
        }
      })
      return filesCount
    },
  },

  async created() {
    this.institutionId = JSON.parse(localStorage.getItem('institution')).id
    this.list()
    this.getNatures()
    this.getUnidades()
    this.getDepartments()
    this.getCostCenters() // Carrega todos os centros de custo inicialmente
  },

  watch: {
    'filter.unidadeId'() {
      this.filterDepartmentsByUnit()
      this.getCostCenters()
    },
    pageSize() {
      this.filter.pageSize = this.pageSize
      this.filter.page = 1
      this.list()
    },
    page() {
      this.filter.page = this.page
      this.list()
    },
  },

  methods: {
    async getNatures() {
      await this.apiService
        .get('avoidedCostNature/list')
        .then((response) => {
          this.natures = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar naturezas')
        })
    },

    async getUnidades() {
      this.unidadeLoading = true
      await this.apiService
        .get(`unidade/list/${this.institutionId}`)
        .then((response) => {
          this.unidades = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar unidades')
        })
      this.unidadeLoading = false
    },

    async getDepartments() {
      this.departmentLoading = true
      await this.apiService
        .get(`department/listActive/${this.institutionId}`)
        .then((response) => {
          this.allDepartments = response.content
          this.departments = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar departamentos')
        })
      this.departmentLoading = false
    },

    async getCostCenters() {
      let endpoint
      
      if (!this.filter.unidadeId) {
        // Se não há unidade selecionada, buscar todos os centros de custo da instituição
        endpoint = `CostCenter/listActive/${this.institutionId}`
      } else {
        // Se há unidade selecionada, filtrar por unidade
        endpoint = `CostCenter/listActiveByUnidade/${this.institutionId}/${this.filter.unidadeId}`
      }

      await this.apiService
        .get(endpoint)
        .then((response) => {
          this.costCenters = response.content
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar centros de custo')
        })
    },

    filterDepartmentsByUnit() {
      if (!this.filter.unidadeId) {
        this.departments = this.allDepartments
      } else {
        this.departments = this.allDepartments.filter(
          (dept) => dept.unidadeId === this.filter.unidadeId
        )
      }
      if (this.filter.departmentId) {
        const stillAvailable = this.departments.some(
          (dept) => dept.id === this.filter.departmentId
        )
        if (!stillAvailable) {
          this.filter.departmentId = null
        }
      }
      // Limpar centro de custo quando unidade mudar
      if (this.filter.costCenterId) {
        this.filter.costCenterId = null
      }
    },

    formatMonthDisplay(isoMonth) {
      if (!isoMonth) return ''
      const [year, month] = isoMonth.split('-')
      return `${month}/${year}`
    },

    getFirstDayOfMonth(isoMonth) {
      if (!isoMonth) return null
      return `${isoMonth}-01`
    },

    getLastDayOfMonth(isoMonth) {
      if (!isoMonth) return null
      const [year, month] = isoMonth.split('-')
      const lastDay = new Date(year, month, 0).getDate()
      return `${isoMonth}-${String(lastDay).padStart(2, '0')}`
    },

    saveStartMonth(value) {
      this.startMonthISO = value
      this.filter.startMonth = this.formatMonthDisplay(value)
      this.filter.startDate = this.getFirstDayOfMonth(value)
      this.startMonthMenu = false
    },

    saveEndMonth(value) {
      this.endMonthISO = value
      this.filter.endMonth = this.formatMonthDisplay(value)
      this.filter.endDate = this.getLastDayOfMonth(value)
      this.endMonthMenu = false
    },

    selectAll() {
      this.avoidedCosts.forEach((item) => {
        item.selected = false
        if (item.uploadDocuments) {
          item.uploadDocuments = item.uploadDocuments.map((x) => {
            return {
              ...x,
              selected: false,
            }
          })
        }
      })

      if (this.downloadList.length == this.filesNumber) {
        this.downloadList = []
        return
      }

      const ids = this.downloadList.map((x) => x.id)
      this.avoidedCosts.forEach((item) => {
        if (item.uploadDocuments) {
          const downloadsToPush = item.uploadDocuments.filter(
            (x) => !ids.includes(x.id)
          )
          this.downloadList.push(...downloadsToPush)

          item.selected = true
          item.uploadDocuments = item.uploadDocuments.map((x) => {
            return {
              ...x,
              selected: true,
            }
          })
        }
      })
    },

    getMostRecentUploadDate(documents) {
      if (!documents || documents.length === 0) return null
      const dates = documents.map((doc) => new Date(doc.uploadDate))
      return new Date(Math.max(...dates)).toISOString()
    },

    getFile(document) {
      if (document.filePath) {
        if (document.filePath.includes('equipacarefix.blob.core')) {
          const url = document.filePath
          window.open(url)
        } else {
          const url =
            process.env.VUE_APP_BASE_URL +
            document.filePath.replace('wwwroot/', '')
          window.open(url)
        }
      } else {
        this.getDocument(document)
      }
    },

    async getDocument(document) {
      await this.apiService
        .get(`uploadDocument/${document.id}`)
        .then((response) => {
          const fileWindow = window.open()
          const url = 'data:application/pdf;base64,' + response.content.filepath

          fileWindow.document.write(
            `<html>
              <title>${document.fileName}.${document.fileType}</title>
              <body style:"overflow: hidden; margin: 0">
                <object width="100%" width="-webkit-fill-available" height="100%" height="-webkit-fill-available" type="application/pdf" data="${url}"></object>
              </body>
            </html>`
          )
        })
    },

    addAllToDownloadList(item) {
      if (!item.uploadDocuments) return

      if (item.selected) {
        this.downloadList.push(...item.uploadDocuments)
        item.uploadDocuments = item.uploadDocuments.map((x) => {
          return {
            ...x,
            selected: true,
          }
        })
      } else {
        this.downloadList = this.downloadList.filter(
          (x) => !item.uploadDocuments.map((y) => y.id).includes(x.id)
        )
        item.uploadDocuments = item.uploadDocuments.map((x) => {
          return {
            ...x,
            selected: false,
          }
        })
      }
    },

    addToDownloadList(file) {
      if (file.selected) this.downloadList.push(file)
      else this.downloadList = this.downloadList.filter((x) => x.id != file.id)
    },

    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : 'Sem registros'
    },

    formatCurrency(value) {
      if (!value && value !== 0) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value)
    },

    async getZip() {
      if (this.downloadList.length > 25) {
        this.$toast.warning('Máximo de 25 arquivos por vez')
        return
      }
      this.$toast(
        `${this.$t('Baixando arquivos')}: ${this.downloadList.length} ${this.$t(
          'selecionados'
        )}`
      )
      const ids = this.downloadList.map((x) => x.id)

      await this.apiService
        .post(`avoidedCost/files/zip`, { ids })
        .then((response) => {
          var pdfResult = 'data:application/zip;base64,' + response.content
          const link = document.createElement('a')
          link.href = pdfResult
          link.setAttribute('download', `arquivos_avoided_costs_em_lote`)
          document.body.appendChild(link)
          link.click()
          this.downloadList = []
        })
        .catch(() => {
          this.$toast.error('Erro ao baixar arquivos')
        })
    },

    filterTable() {
      this.page = 1
      this.filter.page = 1
      this.list()
    },

    list() {
      this.filter.institutionId = this.institutionId
      this.loading = true

      this.apiService
        .post('avoidedCost/files', this.filter)
        .then((response) => {
          this.avoidedCosts = response.content.data || []
          this.totalPages = response.content.pager.totalPages
          this.totalItems = response.content.pager.totalItems
          this.totalItensOnPage = this.avoidedCosts.length
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          this.$toast.error('Erro ao carregar dados')
        })
    },
  },
}
</script>

<style></style>
